import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ReactNode } from 'react';
import ProcessInfoIcon from '../ProcessInfoIcon';

const PREFIX = 'Path';

const classes = {
  container: `${PREFIX}-container`,
  card: `${PREFIX}-card`,
  selectedCard: `${PREFIX}-selectedCard`,
  contentWrapper: `${PREFIX}-contentWrapper`,
  scrollContainer: `${PREFIX}-scrollContainer`,
  cardLast: `${PREFIX}-cardLast`,
  containerLast: `${PREFIX}-cardLast`,
  cardFirstLevel: `${PREFIX}-cardFirstLevel`,
  selectedCardFirstLevel: `${PREFIX}-cardFirstLevel`,
  idleCard: `${PREFIX}-cardIdle`
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    marginLeft: theme.spacing(1),
    marginRight: '40px'
  },
  [`&.${classes.containerLast}`]: {
    marginRight: theme.spacing(2)
  },
  [`& .${classes.cardFirstLevel}`]: {
    background: theme.palette.info.light,
    borderColor: theme.palette.info.light,
    '&::before': {
      borderBottomColor: theme.palette.info.light
    },
    '&:hover': {
      background: theme.palette.primary.light
    }
  },
  [`& .${classes.selectedCardFirstLevel}`]: {
    background: theme.palette.primary.light,
    borderColor: theme.palette.primary.light
  },
  [`& .${classes.selectedCard}`]: {
    background: theme.palette.primary.light,
    borderColor: theme.palette.primary.light
  },
  [`& .${classes.idleCard}`]: {
    background: theme.palette.info.light,
    borderColor: theme.palette.info.light
  },
  [`& .${classes.card}`]: {
    outline: 0,
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      right: '-40px',
      bottom: 'calc(50% - 4px)',
      width: '30px',
      height: '0',
      borderBottom: '4px dashed',
      borderBottomColor: theme.palette.info.light
    },
    '&:hover': {
      background: theme.palette.primary.light,
      '.ProcessInfo-infoIconDiv': {
        visibility: 'visible'
      }
    }
  },
  [`& .${classes.cardLast}`]: {
    '&::before': {
      display: 'none'
    }
  },
  [`& .${classes.contentWrapper}`]: {
    height: '98px',
    display: 'flex',
    maxWidth: '172px',
    minWidth: '110px',
    justifyContent: 'center',
    alignItems: 'center'
  },

  [`& .${classes.scrollContainer}`]: {
    margin: 'auto',
    maxHeight: '100%',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '7px'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '3px'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555'
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: '3px'
    },
    scrollbarColor: '#888 #f1f1f1',
    scrollbarWidth: 'thin'
  }
}));

interface PathCardProps {
  children?: ReactNode;
  last?: boolean;
  firstLevel?: boolean;
  isSelected: boolean;
  onInfoIconClick: () => void;
}

const getCardClasses = (isSelected: boolean, last: boolean, firstLevel: boolean) => {
  let cardClasses = classes.card;

  if (isSelected) {
    if (firstLevel) {
      cardClasses += ' ';
      cardClasses += classes.selectedCardFirstLevel;
    } else {
      cardClasses += ' ';
      cardClasses += classes.selectedCard;
    }
  } else {
    cardClasses += ' ';
    cardClasses += classes.idleCard;
  }

  if (last) {
    cardClasses += ' ';
    cardClasses += classes.cardLast;
  }

  return cardClasses;
};

const Path = ({
  children,
  isSelected,
  last,
  firstLevel,
  onInfoIconClick,
  ...props
}: PathCardProps) => {
  return (
    <Root className={`${classes.container} ${last ? classes.containerLast : ''}`}>
      <Paper className={getCardClasses(isSelected, !!last, !!firstLevel)}>
        {<ProcessInfoIcon onClick={onInfoIconClick} />}
        <div {...props} data-testid="process">
          <div className={classes.contentWrapper}>
            <div className={classes.scrollContainer}>
              <Typography variant="subtitle1">{children}</Typography>
            </div>
          </div>
        </div>
      </Paper>
    </Root>
  );
};

export default Path;
